<template>
    <div
        id="livesearch"
        class="livesearch"
        @keyup.esc="blur"
        @keyup.down="incrementSelectedResultIndex"
        @keyup.up="decrementSelectedResultIndex"
        @keyup.enter="redirectToSelected"
    >
        <div class="livesearch__input kgr-inputIcon">
            <input
                v-model="q"
                type="text"
                class="kgr-inputIcon__input kgr-input kgr-input--small"
                :placeholder="$t('Suchbegriff')"
                @input="search"
                @focus="focus"
                @click="focus"
            />
            <span class="kgr-inputIcon__icon">
                <i
                    v-show="q.length > 0"
                    class="icon-close"
                    @click="q = ''"
                ></i>
                <i
                    v-show="q.length === 0"
                    class="icon-search"
                    @click="redirectToSearch"
                ></i>
            </span>
        </div>
        <div
            v-show="showResultlist"
            class="results searchresults kgr-region kgr-region--default kgr-region--depth kgr-region--rounded kgr-region--noPadding"
        >
            <div
                v-if="searching"
                class="searching"
            >
                <i class="icon-spinner icon-pulse"></i>
            </div>
            <ul v-if="!searching">
                <li
                    v-for="(result, index) in results.slice(0, numberOfResultsShown)"
                    :key="result.link"
                    class="list-item result-item kgr-region__unpadHorizontally kgr-region"
                    :class="{ last: index === lastResultIndex, active: index === selectedResultIndex }"
                    @click="redirect(result.link)"
                    @mouseover="selectResult(index)"
                >
                    <a
                        v-if="!isFederation && resultLinkActive"
                        :href="result.link"
                        :aria-label="result.title"
                        class="result-link"
                    ></a>
                    <div>
                        <div class="avatar default">
                            <img :src="result.image" />
                        </div>
                    </div>
                    <div>
                        <h4>
                            <kgr-highlight
                                :value="result.title"
                                :search-phrase="q"
                            />
                        </h4>
                        <div v-if="result.category === Result.CATEGORY_MEMBER">
                            <p class="type">
                                <kgr-member-contract-state-icon :state="getMemberContractState(result.contractState)" />
                                {{ result.type }}
                                <template v-if="result.membershipNumber">
                                    (<kgr-highlight
                                        :value="result.membershipNumber"
                                        :search-phrase="q"
                                    />)
                                </template>
                            </p>
                        </div>
                        <div v-else-if="result.category === Result.CATEGORY_CHILD">
                            <p class="type">
                                <kgr-child-status-icon
                                    :status="result.contractStatus"
                                    small
                                    style="float: left"
                                />
                                <span>&nbsp;{{ result.type }} ({{ $t('{years} Jahre', { years: result.age }) }})</span>
                            </p>
                        </div>
                        <p
                            v-else
                            class="type"
                            v-text="result.type"
                        ></p>
                        <p
                            v-if="result.institutionName"
                            v-text="result.institutionName"
                        ></p>
                        <p v-if="result.match">
                            {{ result.match.key }}:
                            <kgr-highlight
                                :value="result.match.value"
                                :search-phrase="q"
                            />
                        </p>
                        <p
                            v-if="isKindergartenEmployee(result)"
                            @mouseover="deactivateResultLink()"
                            @mouseout="activateResultLink()"
                        >
                            <a
                                :href="getEmployeeAbsenceUrl(result)"
                                target="_blank"
                                class="kgr-link"
                            >{{ $t('Abwesenheit erstellen') }}</a>
                        </p>
                    </div>
                </li>
                <li
                    v-show="showMoreResultsItem"
                    class="list-item more-results"
                    :class="{ active: selectedResultIndex === lastResultIndex + 1 }"
                    @click="redirectToSelected"
                    @mouseover="selectMoreResults"
                >
                    <h4 v-text="moreResults"></h4>
                </li>
                <li
                    v-show="showNoResultsItem"
                    class="list-item no-results"
                >
                    <h4>{{ $t('Es wurden keine Ergebnisse gefunden.') }}</h4>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
import ChildStatusIcon from '../../child/contract/ChildStatusIcon';
import Highlight from '../../common/Highlight.vue';
import KgrMemberContractStateIcon from '../../members/MemberContractStateIcon.vue';
import MemberContractState from '../../../js/dto/MemberContractState';
import Result from '../../../js/dto/livesearch/Result';

const MIN_SEARCH_PHRASE_LENGTH = 3;

export default {
    components: {
        'kgr-highlight': Highlight,
        'kgr-child-status-icon': ChildStatusIcon,
        KgrMemberContractStateIcon,
    },
    props: {
        searchPath: {
            type: String,
            required: true,
        },
        searchResultPath: {
            type: String,
            required: true,
        },
        customerId: {
            type: String,
            required: true,
        },
        userId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            q: '',
            maxResults: 9,
            numberOfResultsShown: 8,
            queryOfLastResult: '',
            results: [],
            cache: {},
            hideResults: false,
            searchTimer: null,
            selectedResultIndex: null,
            resultLinkActive: true,
        };
    },
    computed: {
        isFederation() {
            return document.location.pathname.startsWith('/federation');
        },
        Result() {
            return Result;
        },
        localStorageKey() {
            return `${this.customerId}_${this.userId}_livesearch_cache`;
        },
        showMoreResultsItem() {
            return this.results.length > this.numberOfResultsShown;
        },
        showNoResultsItem() {
            return this.q.length > 0 && this.results.length === 0 && !this.searching;
        },
        moreResults() {
            return this.$t('weitere Ergebnisse');
        },
        searching() {
            return this.q.trim().toLowerCase() !== this.queryOfLastResult.trim().toLowerCase();
        },
        showResultlist() {
            return this.q.trim().length >= MIN_SEARCH_PHRASE_LENGTH && !this.hideResults;
        },
        numberOfRows() {
            let numberOfRows = Math.min(8, this.results.length);
            if (this.showMoreResultsItem) {
                numberOfRows++;
            }

            return numberOfRows;
        },
        lastResultIndex() {
            return Math.min(this.numberOfResultsShown, this.results.length) - 1;
        },
    },
    mounted() {
        this.loadCache();
    },
    methods: {
        search(event) {
            if ($.inArray(event.key, ['Escape', 'ArrowDown', 'ArrowUp', 'Enter']) !== -1) {
                return;
            }
            clearTimeout(this.searchTimer);
            if (this.q.trim().length < MIN_SEARCH_PHRASE_LENGTH) {
                return;
            }

            this.hideResults = false;
            this.selectedResultIndex = null;
            if (this.q.trim() === '') {
                this.results = [];
                return;
            }
            for (let i = 0; i < this.cache.values.length; i++) {
                if (this.cache.values[i].q === this.q.toLowerCase()) {
                    this.queryOfLastResult = this.cache.values[i].q.toLowerCase();
                    this.results = this.cache.values[i].results;
                    return;
                }
            }

            this.searchTimer = setTimeout(() => {
                this.sendSearchRequest();
            }, 600);
        },
        sendSearchRequest() {
            $.get(this.searchPath, { q: this.q, maxResults: this.maxResults }, (data) => {
                this.writeCache(data);
                if (this.q !== data.q) {
                    return;
                }
                this.queryOfLastResult = data.q.toLowerCase();
                this.results = data.results;
            });
        },
        redirect(link) {
            if (!this.resultLinkActive) {
                return;
            }
            window.location.href = link;
        },
        redirectToSelected() {
            if (this.q === '') {
                return;
            }
            if (this.selectedResultIndex === null || this.selectedResultIndex === this.lastResultIndex + 1) {
                this.redirect(`${this.searchResultPath}?q=${this.q}`);
                return;
            }
            this.redirect(this.results[this.selectedResultIndex].link);
        },
        redirectToSearch() {
            this.redirect(this.searchResultPath);
        },
        writeCache(data) {
            this.cache.values.push({ q: data.q.toLowerCase(), results: data.results });
            localStorage.setItem(this.localStorageKey, JSON.stringify(this.cache));
        },
        loadCache() {
            const cache = JSON.parse(localStorage.getItem(this.localStorageKey));
            if (cache && cache.createdAt > Date.now()) {
                this.cache = cache;
            } else {
                this.cache = {
                    createdAt: Math.floor((Date.now() - new Date().getTimezoneOffset()) / 1000),
                    values: [],
                };
            }
        },
        incrementSelectedResultIndex() {
            if (this.results.length === 0) {
                return;
            }
            if (this.selectedResultIndex === null) {
                this.selectedResultIndex = 0;
            } else {
                this.selectedResultIndex++;
            }
            if (this.selectedResultIndex > this.numberOfRows - 1) {
                this.selectedResultIndex = 0;
            }
        },
        decrementSelectedResultIndex() {
            if (this.results.length === 0) {
                return;
            }
            if (this.selectedResultIndex === null) {
                this.selectedResultIndex = this.numberOfRows - 1;
            } else {
                this.selectedResultIndex--;
            }
            if (this.selectedResultIndex < 0) {
                this.selectedResultIndex = this.numberOfRows - 1;
            }
        },
        blur() {
            this.hideResults = true;
        },
        focus() {
            this.hideResults = false;
        },
        selectResult(index) {
            this.selectedResultIndex = index;
        },
        selectMoreResults() {
            this.selectedResultIndex = this.lastResultIndex + 1;
        },
        getMemberContractState(data) {
            return new MemberContractState(data.status, data.contractType);
        },
        isKindergartenEmployee(result) {
            return result.category === Result.CATEGORY_EMPLOYEE
                && result.institutionType === Result.INSTITUTION_TYPE_KINDERGARTEN;
        },
        getEmployeeAbsenceUrl(result) {
            return `/federation/login/employee/${result.identity}/new-absence`;
        },
        deactivateResultLink() {
            this.resultLinkActive = false;
        },
        activateResultLink() {
            this.resultLinkActive = true;
        },
    },
};
</script>

<style rel="stylesheet/scss" lang="scss" scoped>
</style>
